import { Section } from "@/types/common";

export const shortenText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

export const formatDays = (input: string) => {
  // Split the input string by commas and remove duplicates
  const days = [...new Set(input.split(",").map((day) => day.trim()))];

  // Mapping of full day names to abbreviated form
  const dayMap = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun",
  };

  // Get the abbreviated form for each unique day
  const formattedDays = days.map((day) => dayMap[day as keyof typeof dayMap]);

  // Return the formatted string
  return formattedDays.join(", ");
};

export const convertTimeRange = (
  input: string,
  startDate?: string,
  endDate?: string
): string => {
  if (!input) return "";

  // Split the input string by '-'
  const times = input.split("-");

  // Helper function to convert 24-hour format to 12-hour AM/PM format
  function convertTo12Hour(time: string) {
    if (!time || !time.includes(":")) return ""; // Handle invalid input
    const [hour, minute] = time.split(":").map(Number); // Convert to number

    let period = "am";
    let hour12 = hour;

    // Convert to 12-hour format
    if (hour >= 12) {
      period = "pm";
      if (hour > 12) hour12 -= 12; // Convert hours greater than 12 to 12-hour format
    } else if (hour === 0) {
      hour12 = 12; // Handle midnight
    }

    // Format time as "hh:mmam/pm"
    return `${hour12}:${minute.toString().padStart(2, "0")}${period}`;
  }

  // If start and end times are the same AND dates are the same, return just one time
  if (times[0]?.trim() === times[1]?.trim() && startDate === endDate) {
    return convertTo12Hour(times[0]?.trim());
  }

  // Convert both start and end times to 12-hour format
  const startTime = convertTo12Hour(times[0]?.trim());
  const endTime = convertTo12Hour(times[1]?.trim());

  // Return the formatted range
  return `${startTime} - ${endTime}`;
};

export const FirstCategory = (input: string) => {
  const getFirstCategory = (text: string): string => {
    return text.split(",")[0].trim();
  };

  const firstValue = getFirstCategory(input);

  return firstValue;
};

export const getPagePadding = (data: Section[]): string => {
  if (data.length > 0 && data[data.length - 1].template === "premium_section") {
    return "";
  } else {
    return "pb-10 lg:pb-16";
  }
};

export const padding = {
  top: "pt-10 lg:pt-16",
  bottom: "pb-10 lg:pb-16",
  py: "py-10 lg:py-16",
  gapY: "space-y-10 lg:space-y-16",
};

// Add date formatting function
export const formatddmmyy = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString; // Return original if invalid date

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);

  return `${day}/${month}/${year}`;
};

// Add function for event detail time formatting
// export const formatEventDetailTime = (time: string, startDate?: string, endDate?: string): string => {
//   if (!time || time === "TBA") return "TBA";

//   // Split the input string by '-' (actual format from backend)
//   const times = time.split("-").map(t => t.trim());
//   if (times.length !== 2) return time;

//   // Helper function to convert 24-hour format to 12-hour AM/PM format
//   const convertTo12Hour = (timeStr: string) => {
//     if (!timeStr || !timeStr.includes(":")) return timeStr;

//     // Remove seconds if present (HH:MM:SS -> HH:MM)
//     const timeWithoutSeconds = timeStr.split(":").slice(0, 2).join(":");
//     const [hourStr, minuteStr] = timeWithoutSeconds.split(":");

//     const hour = parseInt(hourStr, 10);
//     const minute = parseInt(minuteStr, 10);

//     if (isNaN(hour) || isNaN(minute)) return timeStr;

//     let period = "AM";
//     let hour12 = hour;

//     // Convert to 12-hour format
//     if (hour >= 12) {
//       period = "PM";
//       if (hour > 12) hour12 -= 12;
//     } else if (hour === 0) {
//       hour12 = 12; // Handle midnight
//     }

//     // Format time as "h:mmam/pm"
//     return `${hour12}:${minute.toString().padStart(2, "0")}${period}`;
//   };

//   // If start and end times are the same AND dates are the same, return just one time
//   if (times[0] === times[1] && startDate === endDate) {
//     return convertTo12Hour(times[0]);
//   }

//   // Convert both start and end times to 12-hour format
//   const startTime = convertTo12Hour(times[0]);
//   const endTime = convertTo12Hour(times[1]);

//   // Return the formatted range
//   return `${startTime} - ${endTime}`;
// };

export const podcastDate = (dateString: string): string => {
  const date = new Date(dateString);

  // Get month name (3 letters uppercase)
  const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();

  // Get day and year
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);

  // Combine in desired format
  return `${day}-${month}-${year}`;
};

export const getOrdinalSuffix = (day: string) => {
  const num = parseInt(day);
  // if (num >= 11 && num <= 13) return "th";
  if (num >= 11 && num <= 13) return `${day}th`;

  const lastDigit = num % 10;
  switch (lastDigit) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

// Format event time based on start_date and end_date
export const formatEventTime = (
  start_date: string,
  end_date?: string,
  separator: "to" | "-" = "to"
): string => {
  if (!start_date) return "";

  try {
    const start = new Date(start_date);

    if (isNaN(start.getTime())) return "";

    // Helper function to convert time to 12-hour format with actual minutes
    const formatTime = (date: Date): string => {
      const hours = date.getHours();
      const minutes = date.getMinutes();

      let period = "am";
      let hour12 = hours;

      // Convert to 12-hour format
      if (hours >= 12) {
        period = "pm";
        if (hours > 12) hour12 -= 12;
      } else if (hours === 0) {
        hour12 = 12; // Handle midnight
      }

      // Format as "h:mmam/pm" with actual minutes
      return `${hour12}:${minutes.toString().padStart(2, "0")}${period}`;
    };

    // If no end_date provided, return just the start time
    if (!end_date) {
      return formatTime(start);
    }

    const end = new Date(end_date);

    if (isNaN(end.getTime())) return formatTime(start);

    // Check if dates are the same (compare only date part, not time)
    const startDateOnly = start.toDateString();
    const endDateOnly = end.toDateString();
    const isSameDate = startDateOnly === endDateOnly;

    const isSameTime =
      start.getHours() === end.getHours() &&
      start.getMinutes() === end.getMinutes();

    // console.log("formatEventTime debug:", {
    //   start_date,
    //   end_date,
    //   startLocal: start.toString(),
    //   endLocal: end.toString(),
    //   startTime: `${start.getHours()}:${start.getMinutes()}`,
    //   endTime: `${end.getHours()}:${end.getMinutes()}`,
    //   isSameDate,
    //   isSameTime
    // });

    // If same date and same time, return just one time
    if (isSameDate && isSameTime) {
      return formatTime(start);
    }

    // Otherwise return time range
    const startTime = formatTime(start);
    const endTime = formatTime(end);

    return `${startTime} ${separator} ${endTime}`;
  } catch (error) {
    console.error("Error formatting event time:", error);
    return "";
  }
};

export const formatEventDate = (
  start_date: string,
  end_date?: string
): string => {
  // Return empty string if no start date is provided
  if (!start_date) return "";

  try {
    // Convert start date string to Date object
    const start = new Date(start_date);

    // Return empty string if start date is invalid
    if (isNaN(start.getTime())) return "";

    const formatSingleDate = (date: Date): string => {
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    };

    // If no end_date provided, return just the formatted start date
    if (!end_date) {
      return formatSingleDate(start);
    }

    // Convert end date string to Date object
    const end = new Date(end_date);

    // If end date is invalid, return just the formatted start date
    if (isNaN(end.getTime())) return formatSingleDate(start);

    // Compare only the date parts (ignoring time) to check if dates are the same
    const startDateOnly = start.toDateString();
    const endDateOnly = end.toDateString();
    const isSameDate = startDateOnly === endDateOnly;

    // If dates are the same (regardless of time), return just one formatted date
    if (isSameDate) {
      return formatSingleDate(start);
    }

    // For different dates, return a date range
    const startDate = formatSingleDate(start);
    const endDate = formatSingleDate(end);

    return `${startDate} to ${endDate}`;
  } catch (error) {
    // Log error and return empty string for any unexpected errors
    console.error("Error formatting event date:", error);
    return "";
  }
};

export const redirectGmap = (location: string) => {
  const gLoc = location?.replace(/ /g, "+");
  const url = `https://www.google.com/maps?q=${gLoc}`;
  return url;
};

export function formatTimeRangeUTC(
  startDate: string,
  endDate?: string | null,
  separator: "to" | "-" = "to"
): string {
  if (!startDate) return "";

  const formatUTC12Hour = (dateStr: string) => {
    const date = new Date(dateStr);

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";

    hours = hours % 12;
    hours = hours === 0 ? 12 : hours;

    return `${hours}:${minutes}${ampm}`;
  };

  const start = formatUTC12Hour(startDate);

  const end = endDate ? formatUTC12Hour(endDate) : null;

  return end ? `${start} ${separator} ${end}` : start;
}

export function MRconvertTimeRange(range: string) {
  const to12HourFormat = (timeStr: string) => {
    const [hour, minute] = timeStr.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const [startTime, endTime] = range.split("-");
  return `${to12HourFormat(startTime)} - ${to12HourFormat(endTime)}`;
}

export function USconvertTimeRange(
  range: string,
  show_start_time?: boolean,
  show_end_time?: boolean
) {
  const to12HourFormat = (timeStr?: string) => {
    if (!timeStr) return "";
    const [hour, minute] = timeStr.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const [startTime, endTime] = range.includes("-")
    ? range.split("-").map((s) => s.trim())
    : [range];

  if (show_start_time && show_end_time) {
    return `${to12HourFormat(startTime)} - ${to12HourFormat(endTime)}`;
  } else if (show_start_time && startTime) {
    return to12HourFormat(startTime);
  } else if (show_end_time && endTime) {
    return to12HourFormat(endTime);
  }
  return "";
}

export function isSameDate(
  published_time: string,
  modified_time: string
): boolean {
  const publishedDate = new Date(published_time);
  const modifiedDate = new Date(modified_time);

  return (
    publishedDate.getFullYear() === modifiedDate.getFullYear() &&
    publishedDate.getMonth() === modifiedDate.getMonth() &&
    publishedDate.getDate() === modifiedDate.getDate()
  );
}

export function extractDatePart(
  utcString: string,
  part: "year" | "month"
): number | string {
  const date = new Date(utcString);

  if (part === "year") {
    return date.getUTCFullYear();
  }

  if (part === "month") {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return monthNames[date.getUTCMonth()];
  }

  throw new Error("Invalid part. Use 'year' or 'month'.");
}

export const formatExternalUrl = (url: string): string => {
  if (!url || url === "#") return "#";

  // If URL already has a protocol, return as is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // If URL starts with www., add https://
  if (url.startsWith("www.")) {
    return `https://${url}`;
  }

  // For any other URL, assume it's relative and return as is
  return url;
};
